// TimeSlots.jsx - Simple availability display
import React, { memo } from "react";
import styled from "@emotion/styled";
import {
  Box,
  Typography,
  Paper,
  Alert,
  Badge,
  Chip,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  IconButton,
  alpha,
} from "@mui/material";
import {
  Event as EventIcon,
  AccessTime as AccessTimeIcon,
  EventAvailable as EventAvailableIcon,
  Language as LanguageIcon,
} from "@mui/icons-material";
import { format } from "date-fns";

// Styled Components
const TimeSlotCard = styled(Paper)(({ theme, isBooked }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(1.5),
  borderRadius: 8,
  border: `1px solid ${alpha(
    isBooked ? theme.palette.error.main : theme.palette.success.main,
    0.3
  )}`,
  backgroundColor: alpha(
    isBooked ? theme.palette.error.main : theme.palette.success.main,
    0.04
  ),
  transition: "transform 0.2s ease, box-shadow 0.2s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: `0 3px 10px ${alpha(
      isBooked ? theme.palette.error.main : theme.palette.success.main,
      0.1
    )}`,
  },
}));

const StatusChip = styled(Chip)(({ theme, color = "primary" }) => ({
  height: 24,
  fontSize: "0.75rem",
  fontWeight: 600,
  backgroundColor: alpha(theme.palette[color].main, 0.12),
  color: theme.palette[color].main,
  "& .MuiChip-label": {
    padding: "0 8px",
  },
}));

// Days of the week in order
const DAYS_OF_WEEK = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

/**
 * TimeSlots Component
 *
 * Displays booked slots and availability schedule for the selected member
 */
const TimeSlots = memo(
  ({
    selectedDate,
    selectedDateBookings,
    availabilityData,
    loadingAvailability,
    activeMemberId,
    theme,
  }) => {
    // Format date for display
    const formatDate = (date) => {
      return format(date, "EEEE, MMMM d, yyyy");
    };

    // Get day schedule display - similar to EventType Availability
    const getDaySchedule = (day) => {
      if (!availabilityData) return "No availability data";

      const isDayAvailable = availabilityData.days?.includes(day);

      if (!isDayAvailable) return "Unavailable";

      // First, try to use dayTimeSlots for day-specific time slots
      if (
        availabilityData.dayTimeSlots &&
        availabilityData.dayTimeSlots.length > 0
      ) {
        const daySlot = availabilityData.dayTimeSlots.find(
          (dts) => dts.day === day
        );

        if (daySlot && daySlot.slots && daySlot.slots.length > 0) {
          // Return all time slots for this day, joined with comma
          return daySlot.slots
            .map((slot) => `${slot.timeStart} - ${slot.timeEnd}`)
            .join(", ");
        }
      }

      // Fallback to the old timeSlots array (applies to all days)
      if (availabilityData.timeSlots && availabilityData.timeSlots.length > 0) {
        return availabilityData.timeSlots
          .map((slot) => `${slot.timeStart} - ${slot.timeEnd}`)
          .join(", ");
      }

      return "No time slots defined";
    };

    // Render time slots section
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography
          variant="h6"
          fontWeight={600}
          sx={{
            borderBottom: `2px solid ${theme.palette.primary.main}`,
            pb: 1,
            mb: 3,
            display: "inline-block",
          }}
        >
          {formatDate(selectedDate)}
        </Typography>

        {loadingAvailability ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={30} />
            <Typography variant="body2" color="textSecondary" ml={2}>
              Loading availability...
            </Typography>
          </Box>
        ) : !activeMemberId ? (
          <Alert
            severity="info"
            icon={<EventAvailableIcon />}
            sx={{ borderRadius: 2, mt: 2 }}
          >
            <Typography variant="body1" mb={0.5}>
              No member selected
            </Typography>
            <Typography variant="body2">
              Please select a team member to view their availability.
            </Typography>
          </Alert>
        ) : (
          <Grid container spacing={3}>
            {/* Booked Slots Column */}
            <Grid item xs={12} md={6}>
              <Box
                display="flex"
                alignItems="center"
                mb={2}
                pb={1}
                borderBottom={`1px solid ${theme.palette.divider}`}
              >
                <Badge
                  badgeContent={selectedDateBookings.length}
                  color="error"
                  max={99}
                  sx={{ mr: 1.5 }}
                >
                  <EventIcon color="error" />
                </Badge>
                <Typography variant="subtitle1" fontWeight={600}>
                  Booked Slots
                </Typography>
              </Box>

              {selectedDateBookings.length === 0 ? (
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: alpha(
                      theme.palette.background.default,
                      0.5
                    ),
                    border: `1px dashed ${theme.palette.divider}`,
                    textAlign: "center",
                  }}
                >
                  <Typography variant="body2" color="textSecondary">
                    No bookings for this date
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ maxHeight: "400px", overflowY: "auto", pr: 1 }}>
                  {selectedDateBookings.map((booking, index) => (
                    <TimeSlotCard
                      key={`booking-${booking.ownerId}-${booking.timeStart}-${index}`}
                      isBooked={true}
                      theme={theme}
                    >
                      <Box display="flex" alignItems="center" mb={1}>
                        <AccessTimeIcon
                          fontSize="small"
                          sx={{ color: theme.palette.error.main, mr: 1 }}
                        />
                        <Typography variant="body2" fontWeight={600}>
                          {booking.timeStart} - {booking.timeEnd}
                        </Typography>
                      </Box>

                      <Box display="flex" alignItems="center" mb={1.5}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {booking.ownerName}
                        </Typography>
                        <StatusChip
                          label={booking.eventTitle}
                          size="small"
                          color="error"
                          sx={{ ml: 1.5 }}
                          theme={theme}
                        />
                      </Box>

                      <Typography variant="body2" color="textSecondary">
                        {booking.customerName}
                        {booking.customerEmail && (
                          <Typography
                            variant="caption"
                            component="span"
                            sx={{ ml: 0.5 }}
                          >
                            ({booking.customerEmail})
                          </Typography>
                        )}
                      </Typography>
                    </TimeSlotCard>
                  ))}
                </Box>
              )}
            </Grid>

            {/* Availability Schedule Column */}
            <Grid item xs={12} md={6}>
              <Box
                display="flex"
                alignItems="center"
                mb={2}
                pb={1}
                borderBottom={`1px solid ${theme.palette.divider}`}
              >
                <EventAvailableIcon color="success" sx={{ mr: 1.5 }} />
                <Typography variant="subtitle1" fontWeight={600}>
                  Availability Schedule
                </Typography>
              </Box>

              {!availabilityData ? (
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: alpha(
                      theme.palette.background.default,
                      0.5
                    ),
                    border: `1px dashed ${theme.palette.divider}`,
                    textAlign: "center",
                  }}
                >
                  <Typography variant="body2" color="textSecondary">
                    No availability schedule found for this member
                  </Typography>
                </Box>
              ) : (
                <Box>
                  {/* Availability Name */}
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    sx={{ mb: 2, color: theme.palette.primary.main }}
                  >
                    {availabilityData.name || "Default Schedule"}
                  </Typography>

                  {/* Weekly Schedule Table */}
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        {DAYS_OF_WEEK.map((day) => {
                          const isAvailable =
                            availabilityData.days?.includes(day);
                          return (
                            <TableRow key={day}>
                              <TableCell
                                sx={{
                                  borderBottom: "none",
                                  py: 1,
                                  width: "120px",
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  fontWeight={600}
                                  sx={{
                                    textDecoration: isAvailable
                                      ? "none"
                                      : "line-through",
                                    color: isAvailable
                                      ? "text.primary"
                                      : "text.disabled",
                                  }}
                                >
                                  {day}
                                </Typography>
                              </TableCell>
                              <TableCell
                                sx={{
                                  borderBottom: "none",
                                  py: 1,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  color={
                                    isAvailable
                                      ? "text.secondary"
                                      : "text.disabled"
                                  }
                                >
                                  {getDaySchedule(day)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Timezone Info */}
                  <Box
                    sx={{
                      mt: 2,
                      p: 1.5,
                      backgroundColor: alpha(theme.palette.grey[500], 0.08),
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <IconButton size="small" sx={{ mr: 0.5, p: 0.5 }}>
                      <LanguageIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="body2" color="textSecondary">
                      {availabilityData.timezone || "UTC"}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Grid>
          </Grid>
        )}
      </Paper>
    );
  }
);

export default TimeSlots;
